import { apiClient, idempotentConfig, unwrap } from './client';
import type { ApiEnvelope } from './types';

/**
 * API-26: the price book (standard cost per capability family and provider), per-hit cost, and vendor statements with
 * their reconciliation. contracts/proposed/openapi/PROPOSAL-api-26-metering-parity.md. Money is integer micros of the
 * named currency; the console never computes a price.
 */

export type BillingMode = 'OFF' | 'ENFORCED';

export interface PriceRow {
  priceId: string;
  family: string;
  provider: string;
  state: 'PRICED' | 'WITHDRAWN';
  standardCostMicros: number | null;
  standardCurrency: string | null;
  vendorCostMicros: number | null;
  vendorCurrency: string | null;
  setBy: string;
  setAt: string;
}

export interface PriceBook {
  hub: string;
  billing: { mode: BillingMode; setBy: string | null; setAt: string | null };
  prices: PriceRow[];
  history: PriceRow[];
}

export interface PriceRequest {
  family: string;
  provider: string;
  standardCostMicros?: number;
  standardCurrency?: string;
  vendorCostMicros?: number;
  vendorCurrency?: string;
  withdraw?: boolean;
}

export interface CostLine {
  family: string;
  provider: string;
  billableCalls: number;
  unpricedCalls: number;
  standardCurrency: string | null;
  standardCostMicros: number;
  vendorCurrency: string | null;
  bookedVendorCostMicros: number;
  adapterCostMicrosUsd: number;
}

export interface CapabilityCosts {
  hub: string;
  month: string;
  monthOpen: boolean;
  billingMode: BillingMode;
  lines: CostLine[];
}

export interface StatementLine {
  family: string;
  calls: number;
  vendorUnits: number | null;
  amountMicros: number | null;
}

export interface VendorStatement {
  statementId: string;
  provider: string;
  month: string;
  currency: string;
  reference: string | null;
  format: 'CSV' | 'JSON';
  fileSha256: string;
  lines: StatementLine[];
  importedBy: string;
  importedAt: string;
  alreadyImported: boolean;
}

export interface StatementImport {
  provider: string;
  month: string;
  currency: string;
  reference?: string;
  format: 'CSV' | 'JSON';
  content: string;
}

export interface StatementDifference {
  unit: 'calls' | 'vendorUnits' | 'amountMicros';
  dimension: string;
  statement: number;
  metered: number;
  difference: number;
}

export interface ReconciliationLine {
  family: string;
  statementCalls: number | null;
  meteredCalls: number;
  statementVendorUnits: number | null;
  meteredVendorUnits: number;
  statementAmountMicros: number | null;
  meteredAmountMicros: number | null;
}

export interface ProviderReconciliation {
  provider: string;
  result: 'MATCHES' | 'DIFFERS' | 'NO_STATEMENT';
  statement: VendorStatement | null;
  amountComparedWith: 'BOOKED_VENDOR_COST' | 'ADAPTER_COST_USD' | 'NOT_COMPARED' | null;
  note: string;
  lines: ReconciliationLine[];
  differences: StatementDifference[];
}

export interface StatementReconciliation {
  hub: string;
  month: string;
  monthOpen: boolean;
  providers: ProviderReconciliation[];
}

/** GET /v1/usage/price-book */
export async function getPriceBook(): Promise<PriceBook> {
  const { data } = await apiClient.get<ApiEnvelope<PriceBook>>('/v1/usage/price-book');
  return unwrap(data);
}

/** POST /v1/usage/price-book/prices */
export async function setPrice(request: PriceRequest): Promise<PriceRow> {
  const { data } = await apiClient.post<ApiEnvelope<PriceRow>>('/v1/usage/price-book/prices', request, idempotentConfig());
  return unwrap(data);
}

/** POST /v1/usage/price-book/billing-mode */
export async function setBillingMode(mode: BillingMode): Promise<PriceBook['billing']> {
  const { data } = await apiClient.post<ApiEnvelope<PriceBook['billing']>>(
    '/v1/usage/price-book/billing-mode',
    { mode },
    idempotentConfig(),
  );
  return unwrap(data);
}

/** GET /v1/usage/capability-costs?month=YYYY-MM */
export async function getCapabilityCosts(month?: string): Promise<CapabilityCosts> {
  const { data } = await apiClient.get<ApiEnvelope<CapabilityCosts>>('/v1/usage/capability-costs', {
    params: { month: month || undefined },
  });
  return unwrap(data);
}

/** POST /v1/usage/vendor-statements */
export async function importStatement(request: StatementImport): Promise<VendorStatement> {
  const { data } = await apiClient.post<ApiEnvelope<VendorStatement>>('/v1/usage/vendor-statements', request, idempotentConfig());
  return unwrap(data);
}

/** GET /v1/usage/vendor-statements/reconciliation?month=YYYY-MM */
export async function getStatementReconciliation(month?: string): Promise<StatementReconciliation> {
  const { data } = await apiClient.get<ApiEnvelope<StatementReconciliation>>('/v1/usage/vendor-statements/reconciliation', {
    params: { month: month || undefined },
  });
  return unwrap(data);
}

/** `1.5` → 1500000. Exact on the string, never through a float. Null when it is not a non-negative decimal of ≤ 6 places. */
export function toMicros(value: string): number | null {
  const m = /^(\d+)(?:\.(\d{1,6}))?$/.exec(value.trim());
  if (!m) return null;
  const micros = Number(m[1]) * 1_000_000 + Number((m[2] ?? '').padEnd(6, '0') || '0');
  return Number.isSafeInteger(micros) ? micros : null;
}

/** 1500000 → `1.5`. */
export function fromMicros(micros: number): string {
  const sign = micros < 0 ? '-' : '';
  const abs = Math.abs(micros);
  const whole = Math.floor(abs / 1_000_000);
  const frac = String(abs % 1_000_000).padStart(6, '0').replace(/0+$/, '');
  return `${sign}${whole.toLocaleString('en-US')}${frac ? `.${frac}` : ''}`;
}
